// auth/registerWorkspaceHostAuth.ts
//
// Hands this host shell's already-authenticated getAccessToken() to the
// kciWorkspace Module Federation remote (via its exposed './authBridge')
// so the remote reuses the host's MSAL session/token as single sign-on
// instead of running its own independent login flow. Called from every
// Remote*.tsx lazy loader before the remote component ever renders;
// memoized so concurrent/repeated mounts only register once.

import { getAccessToken } from './msalConfig';

let registerPromise: Promise<void> | null = null;

interface WorkspaceAuthBridge {
  registerHostAccessTokenProvider: (fn: () => Promise<string | null>) => void;
}

function resolveWorkspaceAuthBridge(module: unknown): WorkspaceAuthBridge | null {
  let candidate = module;

  for (let depth = 0; depth < 3; depth += 1) {
    if (!candidate || typeof candidate !== 'object') {
      return null;
    }

    const record = candidate as Record<string, unknown>;
    if (typeof record.registerHostAccessTokenProvider === 'function') {
      return record as unknown as WorkspaceAuthBridge;
    }

    candidate = record.default;
  }

  return null;
}

export function registerWorkspaceHostAuthBridge(module: unknown): void {
  const authBridge = resolveWorkspaceAuthBridge(module);
  if (!authBridge) {
    throw new Error('registerHostAccessTokenProvider export not found in kciWorkspace/authBridge');
  }

  authBridge.registerHostAccessTokenProvider(getAccessToken);
}

export function registerWorkspaceHostAuth(): Promise<void> {
  if (!registerPromise) {
    registerPromise = import(
      // @ts-expect-error kciWorkspace is a Module Federation remote defined in vite.config.ts
      'kciWorkspace/authBridge'
    )
      .then(registerWorkspaceHostAuthBridge)
      .catch((error) => {
        console.error(
          '[kci-core] Failed to register host auth with kciWorkspace remote; it will fall back to its own login flow.',
          error
        );
        registerPromise = null;
      });
  }
  return registerPromise;
}
